import { PALETTE_SIZE } from "../data/types.js";
import {
  createShaderProgram,
  createUnitQuad,
  paletteUniformData,
  requireAttribute,
  requireBuffer,
  UniformLocations,
} from "./shader-program.js";
import type { HalftoneData } from "../data/types.js";
import type { RenderLayer } from "./types.js";
import { WAVE_ORIGIN, type ClipTransform } from "./layout.js";
import { FACE_OP_TYPE, MAX_RIPPLES, RIPPLE_BAND_GLSL, type RenderState } from "./render-state.js";
import { lumaOrder } from "./palette.js";
import { BLEND_GLSL, blendIndex, type BlendMode } from "./blend.js";
import { FACE_EDGE, FACE_EDGE_GLSL } from "./face-edge.js";

/* The face: halftone dots in WebGL2, with the waves' distortion done on the GPU. One draw call
   places all `data.n` round dots (a unit quad, instanced once per dot).

   While waves are live, the vertex shader works out how much each wave's ring (radius,
   thickness, and amplitude, from the CPU) affects each dot, then applies up to 2 face ops from
   the wave's effect chain (`dotScale`, `dotJitter`, `dropout`, `pixelate`, `paletteRoll`). With
   no waves, no ops, or reduced motion, you get the same still face every time. */

const MAX_OPS = 2;

/** The face's band is this many times thicker than everyone else's, so more of the X-ray
    skeleton underneath shows at once (the X-ray's timing uses it too). */
export const FACE_BAND_WIDEN = 2;

// The wave's origin in artwork units (0 to 1024). The band math itself runs in page pixels.
export const ORIGIN_X = WAVE_ORIGIN.x * 1024;
export const ORIGIN_Y = WAVE_ORIGIN.y * 1024;

const glslFloat = (n: number): string => n.toFixed(2);

const VERTEX_SHADER_SRC = `#version 300 es
in vec2 aCorner;     // unit quad corner in [-1,1], per vertex (divisor 0)
in vec2 aCenter;     // dot center in artwork units [0,1024], per instance (divisor 1)
in float aRadius;    // dot radius in artwork units, per instance
in float aColorIdx;  // palette index, per instance
in float aOpacity;   // 0 to 1, per instance: the fade at the outline (outlineFade)
uniform vec4 uTransform;  // (sx, sy, ox, oy): clipX = ax*sx+ox, clipY = ay*sy+oy
uniform float uMinRadius; // smallest dot radius, in clip units
uniform vec2 uOrigin;     // wave origin, in artwork units
uniform float uArtworkToPage; // artwork units to page pixels (band and jitter use pixels)
uniform int uReduced;     // 1 = reduced motion (no distortion)
uniform int uRippleCount;
uniform vec2 uRing[${MAX_RIPPLES}];  // each wave's ring (RenderState.rings), in page pixels
uniform float uAmplitude[${MAX_RIPPLES}];
uniform int uOpCount;
uniform float uOpType[${MAX_OPS}];       // FACE_OP_TYPE (render-state.ts)
uniform float uOpK[${MAX_OPS}];
uniform float uOpEdge[${MAX_OPS}];
uniform float uOpCrest[${MAX_OPS}];
uniform float uOpP0[${MAX_OPS}];
uniform int uLumOrder[${PALETTE_SIZE}];
uniform int uLumRank[${PALETTE_SIZE}];
uniform float uCellRadius; // the largest dot radius (a full cell), in artwork units
uniform float uGrow;       // setLook: 0 to 1, dots grow toward the full cell
uniform float uSoft;       // setLook: 0 to 1, the fraction of the radius the edge fades over
out vec2 vCorner;
flat out int vColorIdx;
flat out float vOpacity;

${RIPPLE_BAND_GLSL}
// The biggest shift of the smoke shuffle, which is always on, in page pixels (times influence).
const float SHUFFLE_PX = 24.0;
// A wave scatters the face so the X-ray skeleton underneath peeks through. In the band, dots
// also shrink by up to SCATTER_SHRINK of their size, and the face's band is FACE_BAND_WIDEN
// times the normal one, so more of the skeleton shows at once. The text and the field keep
// the normal band.
const float SCATTER_SHRINK = 0.5;
const float FACE_BAND_WIDEN = ${glslFloat(FACE_BAND_WIDEN)};

// The shrinking edge (FACE_EDGE).
${FACE_EDGE_GLSL}
const float MIN_KEEP = ${glslFloat(FACE_EDGE.minKeep)};

void main() {
  vCorner = aCorner;
  vOpacity = aOpacity;
  int ci = int(aColorIdx + 0.5);
  vec2 pos = aCenter;
  float rad = aRadius;
  bool dropped = false;
  float infl = 0.0;

  float keep = faceEdgeKeep(aCenter);
  // The look (setLook): on a dark background, the gaps between halftone dots look like a black
  // grid, so the dots can grow toward the full cell. 0 is the plain halftone.
  rad = mix(rad, uCellRadius, uGrow);
  rad *= keep;
  // Tiny dots go away entirely, so no faint row of specks traces the straight edge.
  if (keep < MIN_KEEP) dropped = true;

  if (uReduced == 0 && uRippleCount > 0) {
    float phase = fract(sin(float(gl_InstanceID) * 12.9898) * 43758.5453) * 6.2831853;
    // Band math in page pixels, from the wave's origin (the butterfly). Each wave's ring grows
    // from there, so the scatter spreads outward from the flap.
    vec2 fromOrigin = (aCenter - uOrigin) * uArtworkToPage;
    for (int k = 0; k < ${MAX_RIPPLES}; k++) {
      if (k >= uRippleCount) break;
      // The face's band is FACE_BAND_WIDEN times as thick.
      infl += rippleInfluence(ringOffset(fromOrigin, uRing[k]) / FACE_BAND_WIDEN, uAmplitude[k]);
    }
    infl = min(infl, 1.4);
    rad *= 1.0 - SCATTER_SHRINK * min(infl, 1.0);

    // The base shuffle, which runs no matter what the op chain is: each dot jitters in its own
    // random direction (from the instance phase), scaled by the band influence. So the wave
    // shimmers the dots along its ring, like the dotJitter op but always on. It's per dot,
    // not a shared flow field, so it follows the circle instead of drifting in blobs.
    float shuf = infl * SHUFFLE_PX / uArtworkToPage;
    pos.x += cos(phase * 7.3) * shuf;
    pos.y += sin(phase * 5.1) * shuf;

    if (uOpCount > 0 && infl > 0.02) {
      for (int o = 0; o < ${MAX_OPS}; o++) {
        if (o >= uOpCount) break;
        float a = infl * uOpK[o];
        if (uOpEdge[o] > 0.5) { float aa = min(a, 2.0); a = 1.0 - abs(1.0 - aa); }
        if (a < uOpCrest[o]) continue;
        int ty = int(uOpType[o] + 0.5);
        float p0 = uOpP0[o];
        if (ty == ${FACE_OP_TYPE.dotScale}) {
          rad *= 1.0 + (p0 - 1.0) * a;
        } else if (ty == ${FACE_OP_TYPE.dotJitter}) { // p0 in page pixels, scaled to artwork
          pos.x += cos(phase * 7.3) * p0 * a / uArtworkToPage;
          pos.y += sin(phase * 5.1) * p0 * a / uArtworkToPage;
        } else if (ty == ${FACE_OP_TYPE.dropout}) {
          if (fract(phase * 97.0) < p0 * a) dropped = true;
        } else if (ty == ${FACE_OP_TYPE.pixelate}) { // p0 in page pixels, scaled to artwork
          float g = p0 * 2.0 / uArtworkToPage;
          pos.x += (floor(pos.x / g + 0.5) * g - pos.x) * a;
          pos.y += (floor(pos.y / g + 0.5) * g - pos.y) * a;
        } else if (ty == ${FACE_OP_TYPE.paletteRoll}) {
          int nr = uLumRank[ci] + int(floor((p0 / 12.0) * a + 0.5));
          nr = clamp(nr, 0, ${PALETTE_SIZE - 1});
          ci = uLumOrder[nr];
        }
      }
    }
  }

  vColorIdx = ci;
  if (dropped) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }  // off-screen, so clipped away
  // A soft edge fades out past the dot's radius, so its quad grows by the same fraction.
  float rClip = max(uMinRadius, rad * abs(uTransform.x)) * (1.0 + uSoft);
  vec2 c = vec2(pos.x * uTransform.x + uTransform.z, pos.y * uTransform.y + uTransform.w);
  gl_Position = vec4(c + aCorner * rClip, 0.0, 1.0);
}
`;

const FRAGMENT_SHADER_SRC = `#version 300 es
precision highp float;
in vec2 vCorner;
flat in int vColorIdx;
flat in float vOpacity;
uniform vec3 uPalette[${PALETTE_SIZE}];   // rgb, 0 to 1
uniform float uSoft;
out vec4 fragColor;
${BLEND_GLSL}
void main() {
  // How far out this pixel is in the circle: 1 on its outline, less inside. The dot fades out over
  // one device pixel centered on the outline, so the edge is smooth instead of stair-stepped,
  // and it covers as much as a hard edge would. The quad only clips the outer half pixel where
  // the outline touches it.
  float edge = length(vCorner);
  // The quad has room for the soft edge too (see rClip), so rescale to keep 1 on the outline.
  // A soft edge widens the fade from one device pixel to that fraction of the radius.
  edge *= 1.0 + uSoft;
  float aa = max(0.5 * fwidth(edge), uSoft);
  float coverage = 1.0 - smoothstep(1.0 - aa, 1.0 + aa, edge);
  if (coverage <= 0.0) discard;
  vec3 ink = uPalette[vColorIdx];
  fragColor = vec4(blend(uGround, ink), 1.0) * coverage * vOpacity;
}
`;

/** The (light) palette sorted by luminance, dark to light:
    `order[rank]=paletteIndex`, `rank[paletteIndex]=rank`. */
function buildLumOrdering(colors: readonly string[]): { order: Int32Array; rank: Int32Array } {
  const order = new Int32Array(PALETTE_SIZE);
  const rank = new Int32Array(PALETTE_SIZE);
  const idx = lumaOrder(
    colors,
    colors.map((_, i) => i),
  );
  for (let i = 0; i < idx.length && i < PALETTE_SIZE; i++) order[i] = idx[i]!;
  for (let i = 0; i < idx.length; i++) rank[idx[i]!] = i;
  return { order, rank };
}

const DOT_FIELD_UNIFORMS = [
  "uTransform",
  "uMinRadius",
  "uPalette",
  "uOrigin",
  "uArtworkToPage",
  "uReduced",
  "uRippleCount",
  "uRing",
  "uAmplitude",
  "uOpCount",
  "uOpType",
  "uOpK",
  "uOpEdge",
  "uOpCrest",
  "uOpP0",
  "uLumOrder",
  "uLumRank",
  "uBlend",
  "uGround",
  "uCellRadius",
  "uGrow",
  "uSoft",
] as const;

/** How the dots look (`setLook`). All zeros is the plain halftone. */
export interface DotLook {
  /** 0 to 1: each dot grows toward the full cell, closing the gaps. */
  grow: number;
  /** 0 to 1: the fraction of its radius the dot's edge fades over. */
  soft: number;
}

export const PLAIN_DOTS: Readonly<DotLook> = Object.freeze({
  grow: 0,
  soft: 0,
});

/** Draws the halftone dots in one `drawArraysInstanced` call, distorted by the waves in each
    frame's `RenderState`. */
export class DotFieldLayer implements RenderLayer {
  private readonly gl: WebGL2RenderingContext;
  private readonly program: WebGLProgram;
  private readonly vao: WebGLVertexArrayObject;
  private readonly quadBuffer: WebGLBuffer;
  private readonly instanceBuffer: WebGLBuffer;
  private readonly u: UniformLocations<(typeof DOT_FIELD_UNIFORMS)[number]>;
  private readonly instanceCount: number;
  // Scratch arrays, reused every frame so nothing gets allocated per frame.
  private readonly opType = new Float32Array(MAX_OPS);
  private readonly opK = new Float32Array(MAX_OPS);
  private readonly opEdge = new Float32Array(MAX_OPS);
  private readonly opCrest = new Float32Array(MAX_OPS);
  private readonly opP0 = new Float32Array(MAX_OPS);
  // With no waves, the face doesn't change over time, and the canvas keeps its last frame if
  // nothing draws. So an idle frame with the same inputs skips the draw.
  private idleDrawn = false;
  private idleReducedMotion = false;
  // What draws under the dots in this context (the X-ray), right after the clear.
  private underlay: { draw(): void } | null = null;

  static async create(
    gl: WebGL2RenderingContext,
    data: HalftoneData,
    signal?: AbortSignal,
  ): Promise<DotFieldLayer> {
    const program = await createShaderProgram(gl, VERTEX_SHADER_SRC, FRAGMENT_SHADER_SRC, signal);
    try {
      signal?.throwIfAborted();
      return new DotFieldLayer(gl, data, program);
    } catch (error) {
      gl.deleteProgram(program);
      throw error;
    }
  }

  private constructor(gl: WebGL2RenderingContext, data: HalftoneData, program: WebGLProgram) {
    this.gl = gl;

    const cleanup: Array<() => void> = [];
    try {
      const u = new UniformLocations(gl, program, DOT_FIELD_UNIFORMS, "DotFieldLayer");

      const aCorner = requireAttribute(gl, program, "aCorner", "DotFieldLayer");
      const aCenter = requireAttribute(gl, program, "aCenter", "DotFieldLayer");
      const aRadius = requireAttribute(gl, program, "aRadius", "DotFieldLayer");
      const aColorIdx = requireAttribute(gl, program, "aColorIdx", "DotFieldLayer");
      const aOpacity = requireAttribute(gl, program, "aOpacity", "DotFieldLayer");

      const vao = gl.createVertexArray();
      if (!vao) throw new Error("DotFieldLayer: gl.createVertexArray returned null");
      cleanup.push(() => gl.deleteVertexArray(vao));
      gl.bindVertexArray(vao);

      const quadBuffer = createUnitQuad(gl, aCorner, "DotFieldLayer");
      cleanup.push(() => gl.deleteBuffer(quadBuffer));

      const n = data.n;
      const floats = 5;
      const instanceData = new Float32Array(n * floats);
      for (let i = 0; i < n; i++) {
        instanceData[i * floats] = data.x[i] ?? 0;
        instanceData[i * floats + 1] = data.y[i] ?? 0;
        instanceData[i * floats + 2] = data.r[i] ?? 0;
        instanceData[i * floats + 3] = data.colorIndex[i] ?? 0;
        instanceData[i * floats + 4] = data.opacity?.[i] ?? 1;
      }
      const instanceBuffer = requireBuffer(gl, "DotFieldLayer instance");
      cleanup.push(() => gl.deleteBuffer(instanceBuffer));
      gl.bindBuffer(gl.ARRAY_BUFFER, instanceBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, instanceData, gl.STATIC_DRAW);

      const stride = floats * Float32Array.BYTES_PER_ELEMENT;
      gl.enableVertexAttribArray(aCenter);
      gl.vertexAttribPointer(aCenter, 2, gl.FLOAT, false, stride, 0);
      gl.vertexAttribDivisor(aCenter, 1);
      gl.enableVertexAttribArray(aRadius);
      gl.vertexAttribPointer(
        aRadius,
        1,
        gl.FLOAT,
        false,
        stride,
        2 * Float32Array.BYTES_PER_ELEMENT,
      );
      gl.vertexAttribDivisor(aRadius, 1);
      gl.enableVertexAttribArray(aColorIdx);
      gl.vertexAttribPointer(
        aColorIdx,
        1,
        gl.FLOAT,
        false,
        stride,
        3 * Float32Array.BYTES_PER_ELEMENT,
      );
      gl.vertexAttribDivisor(aColorIdx, 1);
      gl.enableVertexAttribArray(aOpacity);
      gl.vertexAttribPointer(
        aOpacity,
        1,
        gl.FLOAT,
        false,
        stride,
        4 * Float32Array.BYTES_PER_ELEMENT,
      );
      gl.vertexAttribDivisor(aOpacity, 1);

      gl.bindVertexArray(null);
      gl.bindBuffer(gl.ARRAY_BUFFER, null);

      // Uniforms that never change: the origin and the luminance order (from the light palette).
      const { order, rank } = buildLumOrdering(data.colors);
      gl.useProgram(program);
      gl.uniform2f(u.get("uOrigin"), ORIGIN_X, ORIGIN_Y);
      gl.uniform1iv(u.get("uLumOrder"), order);
      gl.uniform1iv(u.get("uLumRank"), rank);
      let cellRadius = 0;
      for (const radius of data.r) cellRadius = Math.max(cellRadius, radius);
      gl.uniform1f(u.get("uCellRadius"), cellRadius);

      this.program = program;
      this.u = u;
      this.vao = vao;
      this.quadBuffer = quadBuffer;
      this.instanceBuffer = instanceBuffer;
      this.instanceCount = n;
    } catch (err) {
      for (const free of cleanup.reverse()) free();
      throw err;
    }
  }

  setPalette(colors: readonly string[]): void {
    this.gl.useProgram(this.program);
    this.gl.uniform3fv(this.u.get("uPalette"), paletteUniformData(colors, PALETTE_SIZE));
    this.invalidate();
  }

  /** Blends the dots over `ground` (sRGB, 0 to 1), the page color behind them, the way the CSS
      blend `mode` would (blend.ts). */
  setBlend(mode: BlendMode, ground: readonly [number, number, number]): void {
    this.gl.useProgram(this.program);
    this.gl.uniform1i(this.u.get("uBlend"), blendIndex(mode));
    this.gl.uniform3f(this.u.get("uGround"), ...ground);
    this.invalidate();
  }

  /** Draws the dots with `look` (see `DotLook`). */
  setLook(look: Readonly<DotLook>): void {
    this.gl.useProgram(this.program);
    this.gl.uniform1f(this.u.get("uGrow"), look.grow);
    this.gl.uniform1f(this.u.get("uSoft"), look.soft);
    this.invalidate();
  }

  /** Draws `underlay` under the dots from the next frame on. */
  setUnderlay(underlay: { draw(): void } | null): void {
    this.underlay = underlay;
    this.invalidate();
  }

  /** Makes the next frame draw, even an idle one: what it shows has changed. */
  invalidate(): void {
    this.idleDrawn = false;
  }

  setTransform(t: ClipTransform): void {
    this.gl.useProgram(this.program);
    this.gl.uniform4f(this.u.get("uTransform"), t.sx, t.sy, t.ox, t.oy);
    this.gl.uniform1f(this.u.get("uMinRadius"), t.minRadius);
    this.invalidate();
  }

  resize(deviceW: number, deviceH: number): void {
    this.gl.viewport(0, 0, deviceW, deviceH);
    this.invalidate();
  }

  render(state: RenderState): void {
    if (this.skipIdle(state)) return;
    const gl = this.gl;
    const u = this.u;
    gl.useProgram(this.program);

    const rippleCount = Math.min(state.rippleCount, MAX_RIPPLES);
    const opCount = Math.min(state.faceOps.length, MAX_OPS);
    for (let i = 0; i < opCount; i++) {
      const op = state.faceOps[i]!;
      this.opType[i] = op.type;
      this.opK[i] = op.k;
      this.opEdge[i] = op.edge;
      this.opCrest[i] = op.crest;
      this.opP0[i] = op.p0;
    }

    gl.uniform1f(u.get("uArtworkToPage"), state.artworkToPage);
    gl.uniform1i(u.get("uReduced"), state.reducedMotion ? 1 : 0);
    gl.uniform1i(u.get("uRippleCount"), rippleCount);
    gl.uniform2fv(u.get("uRing"), state.rings);
    gl.uniform1fv(u.get("uAmplitude"), state.amplitudes);
    gl.uniform1i(u.get("uOpCount"), opCount);
    gl.uniform1fv(u.get("uOpType"), this.opType);
    gl.uniform1fv(u.get("uOpK"), this.opK);
    gl.uniform1fv(u.get("uOpEdge"), this.opEdge);
    gl.uniform1fv(u.get("uOpCrest"), this.opCrest);
    gl.uniform1fv(u.get("uOpP0"), this.opP0);

    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    if (this.underlay) {
      this.underlay.draw();
      gl.useProgram(this.program);
    }
    // The dots' soft edges are premultiplied coverage.
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.bindVertexArray(this.vao);
    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this.instanceCount);
    gl.bindVertexArray(null);
  }

  // True when this idle frame matches the last one drawn. Frames with waves always draw, so
  // the first idle frame after a wave clears away the scattered dots.
  private skipIdle(state: RenderState): boolean {
    if (state.rippleCount > 0) {
      this.idleDrawn = false;
      return false;
    }
    if (this.idleDrawn && this.idleReducedMotion === state.reducedMotion) return true;
    this.idleReducedMotion = state.reducedMotion;
    this.idleDrawn = true;
    return false;
  }

  dispose(): void {
    const gl = this.gl;
    gl.deleteProgram(this.program);
    gl.deleteBuffer(this.quadBuffer);
    gl.deleteBuffer(this.instanceBuffer);
    gl.deleteVertexArray(this.vao);
  }
}
